import { getRandomTestVariant } from '../utils/randomTestVariant';
import { log, logMethodCall } from '../utils/debug';
import {
  WEB_BANNER,
  MULTIPLE_BANNER_BEHAVIOUR,
  SESSION_BANNER_DISPLAY_KEY,
  INT_RADIX,
  OUTBRAIN_CSS_SELECTOR,
  BANNER_BEHAVIOUR,
  CLICK_BANNER_LOCAL_STORAGE_KEY,
  PREVENT_OPTIN_OPEN
} from '../const/common';
import { matchElementPathAndDomain } from '../utils/matchElementPathAndDomain';
import {
  browser,
  os,
  browserVersion,
  browserSubVersion,
  isWebView,
  getScrollPercentage,
  getScrollPixels
} from '../utils/env';
import {
  isWithinDateRange,
  isCurrentTimeInTimeSlots,
  shouldShowBasedOnHoliday
} from './utils';
import Event from '../const/event';
import { detectOutbrainWidgetInViewport } from '../utils/outbrain';
import { setStorageItem, getStorageItem, removeStorageItem } from '../utils/storage';
import { isWebBannerSuppressedByDismissal } from './webBannerDismissal';

const todayDate = new Date().toISOString().split('T')[0];
const storageBannerShownKey = 'cleverpush-webbanners-shown';
const SESSION_LAST_ACTIVE_KEY_PREFIX = 'cleverpush-session-last-active-';

function isSessionCompletedForBanner(banner) {
  if (!banner?.oneSessionDuration) {
    return true;
  }
  const now = Date.now();
  const SESSION_TIMEOUT = (banner.oneSessionDuration) * 60 * 60 * 1000;
  const SESSION_LAST_ACTIVE_KEY = `${SESSION_LAST_ACTIVE_KEY_PREFIX}${banner._id}`;
  const lastActive = parseInt(localStorage.getItem(SESSION_LAST_ACTIVE_KEY), 10) || 0;
  if ((now - lastActive) > SESSION_TIMEOUT) {
    localStorage.setItem(SESSION_LAST_ACTIVE_KEY, now);
    return true;
  }
  return false;
}

export function showWebBannerById(bannerId, testId, context) {
  const bannerParam = (context.config.webBanners || []).find((currentBanner) => currentBanner._id === bannerId);
  if (!bannerParam) {
    log.warn('cleverPushWebBannerId parameter given but banner with that ID could not be found');
    return;
  }

  let bannerShown = false;
  import('./index').then(({ default: WebBanner }) => {
    let banner = bannerParam;
    if (banner.tests) {
      if (testId) {
        const testIndex = banner.tests.findIndex((test) => test.id === testId);
        if (testIndex > -1) {
          banner = { ...banner, ...banner.tests[testIndex], testId: `variant_${testIndex}:${banner.tests[testIndex]?.id}` };
        }
      } else {
        banner = getRandomTestVariant(banner);
      }
    }

    const finalizeShow = () => {
      if (bannerShown) {
        return;
      }

      if (isWebBannerSuppressedByDismissal(banner)) {
        return;
      }

      bannerShown = true;

      const webBanner = new WebBanner(banner, context);
      webBanner.show();

      if (!context.webBanners) {
        context.webBanners = [];
      }
      context.webBanners.push(webBanner);
    };

    if (!isNaN(banner.scrollPercentage) && banner.scrollPercentage > 0) {
      const checkScroll = () => {
        if (getScrollPercentage() >= banner.scrollPercentage || banner.scrollPercentage === 0) {
          window.removeEventListener('scroll', checkScroll);
          finalizeShow();
        }
      };
      window.addEventListener('scroll', checkScroll);
      checkScroll();
    } else if (!isNaN(banner.scrollPixels) && banner.scrollPixels > 0) {
      const checkScroll = () => {
        if (getScrollPixels() >= banner.scrollPixels || banner.scrollPixels === 0) {
          window.removeEventListener('scroll', checkScroll);
          finalizeShow();
        }
      };
      window.addEventListener('scroll', checkScroll);
      checkScroll();
    } else {
      finalizeShow();
    }
  });
}

export async function shouldShowBanner(shownBanner, banner, context) {
  let show = !shownBanner;
  show = await matchElementPathAndDomain(show, banner);

  if (show && banner.minimumVisits) {
    const visitsStr = localStorage.getItem(`cleverpush-banner-${banner._id}-visits`);
    let visits = 0;
    if (visitsStr) {
      const visitsInt = parseInt(visitsStr, INT_RADIX);
      if (!isNaN(visitsInt)) {
        visits = parseInt(visitsInt, INT_RADIX);
      }
    }
    visits += 1;
    localStorage.setItem(`cleverpush-banner-${banner._id}-visits`, `${visits}`);
    show = show && visits >= banner.minimumVisits;
  }

  const finalizeShow = () => {
    const promises = [Promise.resolve(show)];
    const isTrue = (a) => a;
    const allTrue = (values) => values.every(isTrue);
    const choose = (logic) => {
      switch (logic) {
        case 'and': return 'every';
        case 'or': return 'some';
        default: return 'some';
      }
    };

    if (banner.showOnlySubscribers) {
      promises.push(new Promise((resolve) => {
        if (!context.subscriptionManager) {
          resolve(false);
          return;
        }

        context.subscriptionManager.isSubscribed().then(resolve);
      }));
    }

    if (banner.topics && banner.topics.length > 0) {
      const res = new Promise((resolve) => context.getTopics((_null, topics) => {
        resolve(banner.topics[choose(banner.topicsLogic)]((topic) => topics.includes(topic)));
      }));
      promises.push(res);
    }

    if (banner.excludeTopics && banner.excludeTopics.length > 0) {
      const res = new Promise((resolve) => context.getTopics((_null, topics) => {
        resolve(banner.excludeTopics.every((topic) => !topics.includes(topic)));
      }));
      promises.push(res);
    }

    if (banner.tags && banner.tags.length > 0) {
      const hasTag = (tag) => new Promise((resolve) => context.hasTag(tag, resolve));
      const res = Promise.all(banner.tags.map(hasTag))
        .then((values) => values[choose(banner.tagsLogic)](isTrue));
      promises.push(res);
    }

    if (banner.excludeTags && banner.excludeTags.length > 0) {
      const hasTag = (tag) => new Promise((resolve) => !context.hasTag(tag, resolve));
      const res = Promise.all(banner.excludeTags.map(hasTag))
        .then((values) => values.every(isTrue));
      promises.push(res);
    }

    if (banner.attributes && banner.attributes.length > 0) {
      const res = Promise.all(banner.attributes.map((attribute) => new Promise((resolve) => context.hasAttribute(attribute.id, attribute.value, resolve))))
        .then((values) => values.every(isTrue));
      promises.push(res);
    }

    return Promise.all(promises).then(allTrue);
  };

  return new Promise((resolve) => {
    if (show && !isNaN(banner.scrollPercentage) && banner.scrollPercentage > 0) {
      const checkScroll = () => {
        if (getScrollPercentage() >= banner.scrollPercentage || banner.scrollPercentage === 0) {
          window.removeEventListener('scroll', checkScroll);
          finalizeShow().then(resolve);
        }
      };
      window.addEventListener('scroll', checkScroll);
      checkScroll();
    } else if (show && !isNaN(banner.scrollPixels) && banner.scrollPixels > 0) {
      const checkScroll = () => {
        if (getScrollPixels() >= banner.scrollPixels || banner.scrollPixels === 0) {
          window.removeEventListener('scroll', checkScroll);
          finalizeShow().then(resolve);
        }
      };
      window.addEventListener('scroll', checkScroll);
      checkScroll();
    } else {
      finalizeShow().then(resolve);
    }
  });
}

function initOutbrainListener(context) {
  if (!document.querySelector(OUTBRAIN_CSS_SELECTOR)) {
    return;
  }

  context.hasOutbrainWidget = true;

  detectOutbrainWidgetInViewport(OUTBRAIN_CSS_SELECTOR, () => {
    // Close all Banners when Outbrain widget becomes visible
    // eslint-disable-next-line no-restricted-syntax
    for (const webBanner of context.webBanners) {
      if (webBanner.isVisible) {
        webBanner.hide();
      }
    }
  });
}

export function initWebBanners(context) {
  logMethodCall('initWebBanners');

  let params;
  let shouldNotShowOtherBanner = false;
  let promise = Promise.resolve();
  if (typeof URLSearchParams !== 'undefined' && location.search && location.search.length) {
    params = new URLSearchParams(location.search.slice(1));
    if (params.get('cleverPushWebBannerId')) {
      const [bannerId, testId] = params.get('cleverPushWebBannerId').split('-');
      const bannerParam = (context.config.webBanners || []).find((currentBanner) => currentBanner._id === bannerId);
      if (bannerParam) {
        sessionStorage.setItem(PREVENT_OPTIN_OPEN, true);
        context.confirm?.hide();
        showWebBannerById(bannerId, testId, context);
        shouldNotShowOtherBanner = true;
      }
    }
  }

  // App Download Smart Banners
  const forceAppDownloadBanner = params && params.get('cleverPushAppDownloadBanner') === 'true';
  if (
    (
      context.config.appDownloadBanner?.enabled
      && !isWebView()
      && (
        (context.config.appDownloadBanner.androidId && context.config.appDownloadBanner.androidEnabled && os.name === 'Android')
        || (context.config.appDownloadBanner.appleId && context.config.appDownloadBanner.iosEnabled && os.name === 'iOS')
      )
    ) || forceAppDownloadBanner
  ) {
    const isAppDownloadBannerShown = localStorage.getItem('cleverpush-app-download-banner-shown');
    const appDownloadBanner = context.config.appDownloadBanner;
    const bannerSettings = context.config.appDownloadBanner.settings || {};
    if (isAppDownloadBannerShown && !forceAppDownloadBanner) {
      return promise;
    }
    import('../appDownloadBanner/appDownloadBanner').then(({ shouldShowAppDownloadBanner }) => {
      shouldShowAppDownloadBanner(isAppDownloadBannerShown, appDownloadBanner, bannerSettings, context.config, context.subscriptionManager, context.api);
    });
  }

  // iOS Add To Home Screen banner
  // Shows for iOS Safari 16.4+ (as Web Push is supported starting this version)
  const forceHomeScreenBanner = params && params.get('cleverPushHomeScreenBanner') === 'true';
  if (
    (
      context.config.homeScreenBanner?.enabled
      && !isWebView()
      && os.name === 'iOS'
      && browser.name === 'Mobile Safari'
      && (browserVersion > 16 || (browserVersion === 16 && browserSubVersion >= 4))
      && !window.matchMedia('(display-mode: standalone)').matches // check if not already in PWA
    ) || forceHomeScreenBanner
  ) {
    const isHomeScreenBannerShown = localStorage.getItem('cleverpush-home-screen-banner-shown');
    const homeScreenBanner = context.config.homeScreenBanner;
    const bannerSettings = context.config.homeScreenBanner.settings || {};
    if (isHomeScreenBannerShown && !forceHomeScreenBanner) {
      return promise;
    }
    import('../homeScreenBanner/homeScreenBanner').then(({ shouldShowHomeScreenBanner }) => {
      shouldShowHomeScreenBanner(isHomeScreenBannerShown, homeScreenBanner, bannerSettings, context.config, context.api);
    });
  }

  initOutbrainListener(context);

  const shownWebBannersStorage = localStorage.getItem(storageBannerShownKey);

  let shownWebBanners;
  let localBannerIds;
  if (shownWebBannersStorage) {
    try {
      localBannerIds = JSON.parse(shownWebBannersStorage);
      shownWebBanners = [...localBannerIds];
    } catch (error) {
      log.info('error parsing JSON', error);
    }
  }
  if (!shownWebBanners) {
    shownWebBanners = [];
  }
  const shownWebBannersSessionStorage = sessionStorage.getItem(storageBannerShownKey);
  let sessionBannerIds;
  if (shownWebBannersSessionStorage) {
    try {
      sessionBannerIds = JSON.parse(shownWebBannersSessionStorage);
      shownWebBanners = shownWebBanners.concat(JSON.parse(shownWebBannersSessionStorage));
    } catch (error) {
      log.info('error parsing JSON', error);
    }
  }

  const updateStorage = (storage, key, data, bannerId) => {
    if (storage === localStorage) {
      setStorageItem(key, JSON.stringify(data));
    } else {
      storage.setItem(key, JSON.stringify(data));
    }
    shownWebBanners = shownWebBanners.filter((id) => id !== bannerId);
  };

  const handleCleanStorage = (banner) => {
    if (!banner) {
      return;
    }
    const isBannerInSession = (sessionBannerIds || []).includes(banner._id);
    const isBannerInLocal = (localBannerIds || []).includes(banner._id);

    if (isBannerInSession && ![BANNER_BEHAVIOUR.ONCE_PER_SESSION, BANNER_BEHAVIOUR.EVERY_X_SESSIONS, BANNER_BEHAVIOUR.EVERY_X_DAYS].includes(banner.frequency)) {
      sessionBannerIds = sessionBannerIds.filter((id) => id !== banner._id);
      const SESSION_LAST_ACTIVE_KEY = `${SESSION_LAST_ACTIVE_KEY_PREFIX}${banner._id}`;
      if (getStorageItem(SESSION_LAST_ACTIVE_KEY)) {
        removeStorageItem(SESSION_LAST_ACTIVE_KEY);
      }
      updateStorage(sessionStorage, storageBannerShownKey, sessionBannerIds, banner._id);
      const sessionKey = `${storageBannerShownKey}-sessions-${banner._id}`;
      if (getStorageItem(sessionKey)) {
        removeStorageItem(sessionKey);
      }
      if (sessionStorage.getItem(sessionKey)) {
        sessionStorage.removeItem(sessionKey);
      }
    }
    if (isBannerInLocal && ![BANNER_BEHAVIOUR.ONCE, BANNER_BEHAVIOUR.EVERY_X_DAYS].includes(banner.frequency)) {
      localBannerIds = localBannerIds.filter((id) => id !== banner._id);
      updateStorage(localStorage, storageBannerShownKey, localBannerIds);
    }
    if ((banner.frequency === BANNER_BEHAVIOUR.ALWAYS || context.config.env === 'PREVIEW') && shownWebBanners.includes(banner._id)) {
      shownWebBanners = shownWebBanners.filter((id) => id !== banner._id);
    }
  };

  const isBannerShown = (banner) => {
    if (!banner) {
      return false;
    }
    handleCleanStorage(banner);

    if (isWebBannerSuppressedByDismissal(banner)) {
      return true;
    }

    if (banner?.oncePerClick && context.config.env !== 'PREVIEW') {
      const previousClickBanner = getStorageItem(CLICK_BANNER_LOCAL_STORAGE_KEY);
      if (Array.isArray(previousClickBanner) && previousClickBanner.includes(banner._id)) {
        return true;
      }
    }
    if (banner.frequency === BANNER_BEHAVIOUR.X_TIMES_PER_DAY) {
      const bannerKey = `${storageBannerShownKey}-daily-${banner._id}`;
      const dailyData = getStorageItem(bannerKey);
      let dailyCount = 0;
      let lastShownDate = null;

      if (dailyData && typeof dailyData === 'object') {
        try {
          // getStorageItem already returns parsed object
          dailyCount = dailyData.count || 0;
          lastShownDate = dailyData.date;
        } catch (e) {
          log.warn('Error parsing dailyData in isBannerShown:', e);
        }
      }

      if (lastShownDate !== todayDate) {
        dailyCount = 0;
        setStorageItem(bannerKey, JSON.stringify({ count: dailyCount, date: todayDate }));
      }

      const maxTimesPerDay = banner.frequencyInterval || 1;

      if (dailyCount >= maxTimesPerDay) {
        return true;
      }
    }

    if (banner.frequency === BANNER_BEHAVIOUR.EVERY_X_DAYS) {
      const bannerKey = `${storageBannerShownKey}-every-x-days-${banner._id}`;

      const lastShownData = getStorageItem(bannerKey);

      if (lastShownData) {
        try {
          // getStorageItem already returns parsed object, no need to parse again
          const lastShownTimestamp = lastShownData.timestamp;
          const daysInterval = banner.frequencyInterval || 1;
          const millisecondsInDay = 24 * 60 * 60 * 1000;
          const currentTimestamp = Date.now();
          const daysSinceLastShown = (currentTimestamp - lastShownTimestamp) / millisecondsInDay;

          if (daysSinceLastShown < daysInterval) {
            // Not enough days have passed, mark as shown to prevent display
            if (!shownWebBanners.includes(banner._id)) {
              shownWebBanners = [...shownWebBanners, banner._id];
            }
            return true;
          }
          // Enough time has passed - remove from shownWebBanners if present
          if (shownWebBanners.includes(banner._id)) {
            shownWebBanners = shownWebBanners.filter((id) => id !== banner._id);
          }
        } catch (e) {
          log.warn('Error parsing everyXDays data in isBannerShown:', e);
        }
      }
    }

    if (banner.frequency === BANNER_BEHAVIOUR.EVERY_X_SESSIONS) {
      const sessionKey = `${storageBannerShownKey}-sessions-${banner._id}`;
      const sessions = parseInt(localStorage.getItem(sessionKey), 10) || 0;
      if (sessions < banner.frequencyInterval && sessions > 0) {
        if (isSessionCompletedForBanner(banner)) {
          if (!sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, `${sessions + 1}`);
            localStorage.setItem(sessionKey, `${sessions + 1}`);
          }
        }
        if (!shownWebBanners.includes(banner._id)) {
          shownWebBanners = [...shownWebBanners, banner._id];
        }
      }
    }
    if (banner.frequency === BANNER_BEHAVIOUR.ONCE_PER_SESSION && !isSessionCompletedForBanner(banner)) {
      shownWebBanners = [...shownWebBanners, banner._id];
    }

    const isShown = shownWebBanners.includes(banner._id);
    return isShown;
  };

  const bannerShownXTimeUpdate = (banner) => {
    if (banner.frequency === BANNER_BEHAVIOUR.X_TIMES_PER_DAY) {
      const bannerKey = `${storageBannerShownKey}-daily-${banner._id}`;
      const dailyData = getStorageItem(bannerKey);
      let parsedData = { count: 0, date: todayDate };
      if (dailyData && typeof dailyData === 'object') {
        try {
          // getStorageItem already returns parsed object
          parsedData = dailyData;
        } catch (e) {
          log.warn('Error parsing dailyData:', e);
        }
      }
      setStorageItem(bannerKey, JSON.stringify({ count: (parsedData.count || 0) + 1, date: todayDate }));
    }

    if (banner.frequency === BANNER_BEHAVIOUR.EVERY_X_DAYS) {
      const bannerKey = `${storageBannerShownKey}-every-x-days-${banner._id}`;
      const timestamp = Date.now();
      const data = { timestamp };
      setStorageItem(bannerKey, JSON.stringify(data));
    }
  };

  const shownBanner = false;
  const banners = (context.config.webBanners || [])
    .filter(Boolean)
    .sort((bannerA, bannerB) => new Date(bannerA.createdAt).getTime() - new Date(bannerB.createdAt).getTime())
    .filter((banner) => banner.status !== 'draft' && !isBannerShown(banner));

  if (shouldNotShowOtherBanner) {
    return promise;
  }

  if (banners.length) {
    promise = import('./index').then(({ default: WebBanner }) => {
      (async () => {
        async function initMobileExitIntentListenersWrapper(banner, webBanner) {
          await import('../utils/scriptFunctions').then(({ initMobileExitIntentListeners }) => {
            initMobileExitIntentListeners(WEB_BANNER, shownBanner, banner, webBanner, shownWebBanners, storageBannerShownKey, context);
          });
        }

        async function shouldShowBannerWrapper(banner, webBanner) {
          await shouldShowBanner(shownBanner, banner, context).then((show) => {
            if (show) {
              import('./utils').then(({ checkBannerOptinShow }) => {
                checkBannerOptinShow(banner, context);
              });

              if (banner.exitIntent && !context.isMobile) {
                context.hasExitIntentForWebBanner = true;
              }

              if (banner.multipleBannersBehaviour === MULTIPLE_BANNER_BEHAVIOUR.PER_SESSION) {
                sessionStorage.setItem(SESSION_BANNER_DISPLAY_KEY, banner._id);
              }
              if (banner.multipleBannersBehaviour === MULTIPLE_BANNER_BEHAVIOUR.PER_IMPRESSION) {
                context.multipleBannerOncePerImpressionShown = true;
              }

              bannerShownXTimeUpdate(banner);

              const displayTimeout = setTimeout(() => {
                context.unregisterPendingWebBannerTimeout?.(displayTimeout);
                if (document.getElementById(`cleverpush-banner-${banner._id}`)) {
                  return;
                }
                context.displayElement(webBanner, shownWebBanners, banner, storageBannerShownKey, Event.BANNER_SHOWN);
              }, (banner.timeout || 0) * 1000);
              context.registerPendingWebBannerTimeout?.(displayTimeout);
            }
          });
        }

        const processBanner = async (bannerParam) => {
          const banner = getRandomTestVariant(bannerParam);
          const webBanner = new WebBanner(banner, context);
          const bypassExitIntent = context.isMobile
            && banner.enableRegularDisplayForMobile
            && !banner.exitIntentForMobile;
          const shouldShowOnHoliday = await shouldShowBasedOnHoliday(banner);

          if (
            sessionStorage.getItem(SESSION_BANNER_DISPLAY_KEY)
            || context.multipleBannerOncePerImpressionShown
          ) {
            return;
          }

          if (
            !isWithinDateRange(banner)
            || !shouldShowOnHoliday
            || !isCurrentTimeInTimeSlots(banner?.timetable)
          ) {
            return;
          }

          if (banner.exitIntent && !bypassExitIntent) {
            if (context.hasExitIntentForWebBanner) {
              return;
            }

            if (!(await matchElementPathAndDomain(!shownBanner, banner, true))) {
              return;
            }

            if (context.isMobile && banner.exitIntentForMobile && !banner.enableRegularDisplayForMobile) {
              await initMobileExitIntentListenersWrapper(banner, webBanner);
            }
            context.hasExitIntentForWebBanner = true;
            context.initElementExitIntentListener(WEB_BANNER, shownBanner, banner, webBanner, shownWebBanners, storageBannerShownKey);
          } else {
            await shouldShowBannerWrapper(banner, webBanner);
          }

          (context.webBanners || []).push(webBanner);
        };

        await banners.reduce(
          (promiseData, bannerParam) => promiseData.then(() => processBanner(bannerParam)),
          Promise.resolve({})
        );
      })().catch((err) => {
        log.error(err);
      });
    });
  }

  return promise;
}
